import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import styled, { keyframes } from "styled-components";
import { useAuth } from "../utils";
import BulletinForm from "../components/bulletin/BulletinForm";
import BulletinList from "../components/bulletin/BulletinList";

const BulletinPage = () => {
    const [bulletins, setBulletins] = useState([]); // Initialize with an empty array to prevent undefined errors
    const [loading, setLoading] = useState(false); // New loading state
    const { isAuthenticated, getBulletins, saveBulletin, deleteBulletin } = useAuth();

    // Fetch all bulletins on component mount
    useEffect(() => {
        const fetchBulletins = async () => {
            setLoading(true); // Start loading
            try {
                const data = await getBulletins();
                setBulletins(data || []); // Set data or an empty array if no data returned
            } catch (error) {
                console.error("Error fetching bulletins:", error);
            } finally {
                setLoading(false); // End loading
            }
        };
        fetchBulletins();
    }, []);

    // Save new bulletin
    const handleSaveBulletin = async (newBulletin) => {
        if (!newBulletin.trim()) { // Check if bulletin content is empty
            Swal.fire({
                icon: 'warning',
                title: 'Bulletin content cannot be empty',
                confirmButtonColor: '#3085d6',
            });
            return;
        }
        try {
            setLoading(true); // Start loading before saving
            const result = await saveBulletin(newBulletin);
            setBulletins([result, ...bulletins]); // Add new bulletin to the start of the list
            Swal.fire({
                icon: 'success',
                title: 'Bulletin published successfully!',
                confirmButtonColor: '#ff9800',
            });
        } catch (error) {
            console.error("Error saving bulletin:", error);
            Swal.fire({
                icon: 'error',
                title: 'Failed to publish bulletin',
                text: error.message || 'Please try again later',
                confirmButtonColor: '#3085d6',
            });
        } finally {
            setLoading(false); // End loading after save attempt
        }
    };

    // Delete a bulletin
    const handleDeleteBulletin = async (id) => {
        const confirmation = await Swal.fire({
            title: 'Are you sure you want to delete this bulletin?',
            icon: 'warning',
            showCancelButton: true,
            reverseButtons: true,
            confirmButtonText: 'Yes, delete it',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
        });

        if (confirmation.isConfirmed) {
            setLoading(true); // Start loading before deleting
            const result = await deleteBulletin(id);
            if (result.success) {
                setBulletins(bulletins.filter(bulletin => bulletin.id !== id));
                Swal.fire({
                    icon: 'success',
                    title: 'Bulletin deleted',
                    confirmButtonColor: '#ff9800',
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Unable to delete bulletin',
                    text: result.error || 'Please try again later',
                    confirmButtonColor: '#3085d6',
                });
            }
            setLoading(false); // End loading after delete attempt
        }
    };

    return (
        <div className="container mt-2 mb-5">
            <PageTitle>Bulletin Board</PageTitle>
            {loading ? (
                <PlaceholderContainer>
                    <BouncingText>Loading...</BouncingText>
                </PlaceholderContainer>
            ) : (
                <>
                    {isAuthenticated && <BulletinForm onSave={handleSaveBulletin} />}
                    <BulletinList bulletins={bulletins} onDelete={handleDeleteBulletin} isAuthenticated={isAuthenticated} />
                </>
            )}
        </div>
    );
};

export default BulletinPage;

const PageTitle = styled.h2`
    font-size: 2rem; // 更小的字體讓其在小螢幕上不會過大
    font-weight: bold;
    text-align: center;
    color: var(--app-text, #333);
    margin-bottom: 1.5rem;

    @media (min-width: 768px) {
        font-size: 2.5rem;
    }

    @media (min-width: 1024px) {
        font-size: 3rem;
    }
`;

const bounce = keyframes`
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-10px); }
    60% { transform: translateY(-5px); }
`;

const BouncingText = styled.p`
    text-align: center;
    font-size: 1.5em;
    color: var(--app-text, #333);
    animation: ${bounce} 1.4s infinite;
`;

const PlaceholderContainer = styled.div`
    min-height: 400px; // 設定一個合理的最小高度
    display: flex;
    justify-content: center;
    // align-items: center;
`;
